import { Component, Inject, Input, LOCALE_ID, OnInit } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TabViewModule } from 'primeng/tabview';
import { v4 as uuidv4 } from 'uuid';

import { CLASSIFICATION_OPTIONS } from '../../../../../ordnances';
import { HeroReport } from '../../../model/hero-report/hero-report.model';
import { HeroReportService } from '../../../model/hero-report/hero-report.service';
import { HeroReportImageService } from '../../../model/hero-report-image/hero-report-image.service';
import { HeroReportImageStorageService } from '../../../model/hero-report-image/hero-report-image-storage.service';
import { InputDisplayGridComponent } from '../../../shared/input-display-grid/input-display-grid.component';
import getDataUrlImageType from '../../../utilities/getDataUrlImageType';
import { mgrsCenter, normalizeMgrs } from '../../../utilities/mgrs';
import { SRE_IMAGE_TYPE, SrePhoto, planPhotoChanges, sreDetailsFromForm } from '../../../utilities/sreReport';
import { ItemFormComponent } from '../../hero-report/item-form/item-form.component';
import { SreDetailsFormComponent, buildSreDetailsForm } from '../sre-details-form/sre-details-form.component';
import { SrePhotoGridComponent } from '../sre-photo-grid/sre-photo-grid.component';

/**
 * Viewing and editing an SRE report: an Item tab (possible identification and photos with their
 * captions) and a Details tab (MGRS, depth, parcel, lane, distance, reporter, date and comments).
 * Each tab is edited and saved on its own, as on the UXO report page.
 */
@Component({
  selector: 'app-sre-report-view',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    TabViewModule,
    InputDisplayGridComponent,
    ItemFormComponent,
    SrePhotoGridComponent,
    SreDetailsFormComponent,
  ],
  templateUrl: './sre-report-view.component.html',
})
export class SreReportViewComponent implements OnInit {
  @Input() heroReport!: HeroReport;
  @Input() canEdit = false;

  activeTabIndex = 0;
  currentlyEditingTab: number | null = null;
  isSaving = false;
  saveFailed = false;
  photosTouched = false;

  itemForm!: FormGroup;
  detailsForm!: FormGroup;

  itemRows: { label: string; value: string }[] = [];
  detailRows: { label: string; value: string }[] = [];

  // The photos as saved, and the photos on screen while editing.
  storedPhotos: SrePhoto[] = [];
  editedPhotos: SrePhoto[] = [];

  constructor(
    private fb: FormBuilder,
    private heroReportService: HeroReportService,
    private heroReportImageService: HeroReportImageService,
    private imageStorage: HeroReportImageStorageService,
    @Inject(LOCALE_ID) private locale: string,
  ) {}

  ngOnInit() {
    this.refresh();
    this.loadPhotos();
  }

  get photoError(): boolean {
    return this.photosTouched && this.editedPhotos.length === 0;
  }

  /** Rebuilds the forms and the read-only rows from the report. */
  private refresh() {
    this.itemForm = this.fb.group({
      possibleIdentification: [this.heroReport.possibleIdentification ?? [], Validators.required],
      evacuationPerimeterM: [this.heroReport.evacuationPerimeterM ?? null],
    });
    this.detailsForm = buildSreDetailsForm(this.fb, this.heroReport);

    const classifications = (this.heroReport.possibleIdentification ?? [])
      .map((key) => CLASSIFICATION_OPTIONS.find((option) => option.key === key.trim()))
      .filter((option) => option)
      .map((option) => `${option!.label} ${option!.size}`.trim());

    this.itemRows = [
      {
        label: $localize`:@@sre.classification:Possible Identification`,
        value: classifications.join(', ') || $localize`:@@common.unknown:Unknown`,
      },
    ];

    const report = this.heroReport;
    this.detailRows = [
      { label: $localize`:@@sre.details.mgrs:MGRS`, value: report.mgrs ?? '' },
      { label: $localize`:@@sre.details.depthCm:Depth (cm)`, value: this.numberText(report.depthCm) },
      { label: $localize`:@@sre.details.parcelNumber:Parcel #`, value: report.parcelNumber ?? '' },
      { label: $localize`:@@sre.details.laneNumber:Lane #`, value: report.laneNumber ?? '' },
      { label: $localize`:@@sre.details.distanceMeters:Distance (m)`, value: this.numberText(report.distance) },
      { label: $localize`:@@sre.details.reporterName:Name of Reporter`, value: report.pocName ?? '' },
      { label: $localize`:@@sre.details.reporterTitle:Title of Reporter`, value: report.reporterTitle ?? '' },
      {
        label: $localize`:@@sre.details.date:Date`,
        value: report.firstNoticedDate ? formatDate(report.firstNoticedDate, 'medium', this.locale) : '',
      },
      { label: $localize`:@@sre.details.additionalComments:Additional comments`, value: report.additionalComments ?? '' },
    ];
  }

  private numberText(value: number | null | undefined): string {
    return value === null || value === undefined ? '' : String(value);
  }

  /** Downloads the report's photos from S3 and lists them in order. */
  private async loadPhotos() {
    const images = [...(this.heroReport.heroReportImages ?? [])]
      .filter((image) => image.type !== 'EOD')
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

    const photos = await Promise.all(
      images.map(async (image, index) => ({
        id: image.id,
        url: (await this.imageStorage.getObjectUrl(image)) ?? '',
        caption: image.caption ?? null,
        displayOrder: image.displayOrder ?? index,
      })),
    );

    this.storedPhotos = photos;
    this.editedPhotos = photos;
  }

  editTab(tabIndex: number) {
    this.saveFailed = false;
    this.photosTouched = false;
    this.editedPhotos = this.storedPhotos;
    this.currentlyEditingTab = tabIndex;
  }

  cancelEdit() {
    this.refresh();
    this.editedPhotos = this.storedPhotos;
    this.currentlyEditingTab = null;
  }

  onClassificationSelect(event: { value: string[]; evacuationRadius?: number }) {
    this.itemForm.patchValue({
      possibleIdentification: event.value,
      evacuationPerimeterM: event.evacuationRadius ?? this.itemForm.value.evacuationPerimeterM ?? 0,
    });
  }

  onPhotosChange(photos: SrePhoto[]) {
    this.editedPhotos = photos;
  }

  async saveItemTab() {
    this.itemForm.markAllAsTouched();
    this.photosTouched = true;
    if (this.itemForm.invalid || this.editedPhotos.length === 0) {
      return;
    }

    await this.save(async () => {
      this.heroReport.possibleIdentification = this.itemForm.value.possibleIdentification;
      this.heroReport.evacuationPerimeterM = this.itemForm.value.evacuationPerimeterM ?? 0;
      await this.heroReportService.update(this.heroReport);

      const plan = planPhotoChanges(
        this.storedPhotos.map((photo) => ({
          id: photo.id as string,
          caption: photo.caption,
          displayOrder: photo.displayOrder,
        })),
        this.editedPhotos,
      );

      for (const id of plan.deletes) {
        await this.heroReportImageService.delete(id);
      }
      for (const change of plan.updates) {
        await this.heroReportImageService.updateMetadata(change.id, {
          caption: change.caption,
          displayOrder: change.displayOrder,
        });
      }
      for (const photo of plan.creates) {
        await this.heroReportImageService.createAndUpload({
          id: uuidv4(),
          url: photo.url,
          type: SRE_IMAGE_TYPE,
          fileType: getDataUrlImageType(photo.url),
          heroReportId: this.heroReport.id,
          displayOrder: photo.displayOrder,
          caption: photo.caption,
        });
      }

      // Pick up the saved photos (new ones now have an id and an S3 key).
      this.heroReport = await this.heroReportService.fetchById(this.heroReport.id);
      this.refresh();
      await this.loadPhotos();
    });
  }

  async saveDetailsTab() {
    this.detailsForm.markAllAsTouched();
    if (this.detailsForm.invalid) {
      return;
    }

    await this.save(async () => {
      const previousMgrs = normalizeMgrs(this.heroReport.mgrs);
      const details = sreDetailsFromForm(this.detailsForm.value);
      Object.assign(this.heroReport, details);

      // A changed MGRS moves the report to the center of the new square. An MGRS that was not changed leaves
      // the report where it is.
      const center = details.mgrs && details.mgrs !== previousMgrs ? mgrsCenter(details.mgrs) : null;
      if (center) {
        this.heroReport.latitude = center.latitude;
        this.heroReport.longitude = center.longitude;
      }

      await this.heroReportService.update(this.heroReport);
      this.refresh();
    });
  }

  private async save(action: () => Promise<void>) {
    this.isSaving = true;
    this.saveFailed = false;
    try {
      await action();
      this.currentlyEditingTab = null;
    } catch (error) {
      console.error('Unable to save the SRE report', error);
      this.saveFailed = true;
    } finally {
      this.isSaving = false;
    }
  }
}
